'use client';

import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  ArchivedLifecycleBadge,
  TraceActionButtons,
  isArchivedTracePreview,
  useTraceAgentLabel,
} from '@terra/ui-design-system';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import { rowClickBlockerProps } from '../rowClickBlocker';
import { resolveExecutionObjective } from './executions-list.model';
import { useExecutionsTableContext } from './ExecutionsTableContext';

type ExecutionCellProps = {
  trace: TracePreviewDto;
};

export function ExecutionObjectiveCell({ trace }: ExecutionCellProps) {
  const t = useTranslations('proxy.executions.table');

  return (
    <HStack gap={2} minW={0}>
      {isArchivedTracePreview(trace) ? (
        <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
      ) : null}
      <BodyDefault truncate>
        {resolveExecutionObjective(trace) ?? t('execution')}
      </BodyDefault>
    </HStack>
  );
}

export function ExecutionAgentCell({ trace }: ExecutionCellProps) {
  const getAgentLabel = useTraceAgentLabel();

  return <BodyDefault truncate>{getAgentLabel(trace.name)}</BodyDefault>;
}

export function ExecutionCreatedAtCell({ trace }: ExecutionCellProps) {
  const dateFormatter = useDateFormatter();

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {dateFormatter.format(new Date(trace.createdAt))}
    </BodyDefault>
  );
}

export function ExecutionActionsCell({ trace }: ExecutionCellProps) {
  const {
    resumingTraceIds,
    rerunningTraceIds,
    abortingTraceIds,
    onResumeTrace,
    onRerunTrace,
    onAbortTrace,
    onDeleteTrace,
  } = useExecutionsTableContext();

  return (
    <Box h="full" display="flex" alignItems="center" {...rowClickBlockerProps}>
      <TraceActionButtons
        trace={trace}
        resumingTraceIds={resumingTraceIds}
        rerunningTraceIds={rerunningTraceIds}
        abortingTraceIds={abortingTraceIds}
        onResumeTraceClick={onResumeTrace}
        onRerunTraceClick={onRerunTrace}
        onAbortTraceClick={onAbortTrace}
        onDeleteTraceClick={onDeleteTrace}
      />
    </Box>
  );
}
